/**
 * 任务领域模型（Domain Model）
 *
 * 设计原则：
 * 1. 基于 API DTO，但可以添加前端业务逻辑
 * 2. 不包含 UI 派生字段（那些属于 ViewModel）
 * 3. 可以包含领域方法（如状态判断、业务规则）
 *
 * @version 4.0.0
 * @author Frontend Team
 * @since 2026-01-23
 */

import type {
  TaskDTO,
  TaskItemDTO,
  TTSSegmentDTO,
  BatchFileDTO
} from './task.dto'

// 重新导出类型供外部使用
export type { TaskStatus, TaskType, TaskPriority, BizType, TaskItemStatus } from './task.dto'

// ============= 领域模型接口 =============

/**
 * Task 领域模型
 *
 * 继承 DTO，添加领域方法
 */
export interface Task extends TaskDTO {
  // 继承所有 DTO 字段
}

/**
 * TaskItem 领域模型
 */
export interface TaskItem extends TaskItemDTO {
  // 继承所有 DTO 字段
}

/**
 * TTSSegment 领域模型
 */
export interface TTSSegment extends TTSSegmentDTO {
  // 继承所有 DTO 字段
}

/**
 * BatchFile 领域模型
 */
export interface BatchFile extends BatchFileDTO {
  // 继承所有 DTO 字段
  voice?: string
  audioUrl?: string
  durationSec?: number
  fileSize?: number
}

// ============= 领域方法（纯函数）=============

/**
 * 判断任务是否正在执行
 */
export function isTaskRunning(task: Task): boolean {
  return ['queued', 'processing', 'post_processing'].includes(task.status)
}

/**
 * 判断任务是否已完成
 */
export function isTaskCompleted(task: Task): boolean {
  return task.status === 'completed'
}

/**
 * 判断任务是否失败
 */
export function isTaskFailed(task: Task): boolean {
  return ['processing_failed', 'post_failed'].includes(task.status)
}

/**
 * 判断任务是否可以取消
 */
export function canCancelTask(task: Task): boolean {
  return isTaskRunning(task)
}

/**
 * 判断任务是否可以重试
 */
export function canRetryTask(task: Task): boolean {
  return isTaskFailed(task)
}

/**
 * 判断任务是否可以下载
 */
export function canDownloadTask(task: Task): boolean {
  return task.status === 'completed' && !!task.primaryAssetId
}

/**
 * 获取任务的主要音频 URL
 */
export function getPrimaryAudioUrl(task: Task): string | undefined {
  if (task.type === 'single') {
    // 单次任务：从第一个 item 的 segment 获取
    const firstItem = task.items?.[0]
    return firstItem?.segment?.audioUrl
  } else if (task.type === 'batch') {
    // 批量任务：返回 ZIP 下载链接（需要通过 primaryAssetId 获取）
    return undefined  // 需要调用 API 获取签名 URL
  }
  return undefined
}

/**
 * 获取任务的文本内容
 */
export function getTaskText(task: Task): string | undefined {
  if (task.type === 'single') {
    const firstItem = task.items?.[0]
    return firstItem?.segment?.text
  }
  return task.title
}

/**
 * 获取任务的语音名称
 */
export function getTaskVoice(task: Task): string | undefined {
  if (task.type === 'single') {
    const firstItem = task.items?.[0]
    return firstItem?.segment?.voice
  }
  // 批量任务从 config 获取
  return task.config?.voice
}

/**
 * 计算任务的总时长（秒）
 */
export function calculateTotalDuration(task: Task): number {
  if (!task.items) return 0

  return task.items.reduce((total, item) => {
    // 优先从业务数据获取 (durationSec)
    const duration = item.file?.durationSec || item.segment?.durationSec
    if (duration) return total + duration
    
    // 降级从指标数据获取 (可能名为 duration 或 durationSec)
    const metrics = item.metrics as any
    const metricsDuration = metrics?.duration || metrics?.durationSec || 0
    return total + metricsDuration
  }, 0)
}

/**
 * 计算任务的总文件大小（字节）
 */
export function calculateTotalFileSize(task: Task): number {
  if (!task.items) return 0

  return task.items.reduce((total, item) => {
    // 优先从业务数据获取 (fileSize)
    const size = item.file?.fileSize || item.segment?.fileSize
    if (size) return total + size

    // 降级从指标数据获取 (fileSize)
    const metrics = item.metrics as any
    const metricsSize = metrics?.fileSize || 0
    return total + metricsSize
  }, 0)
}

// ============= 类型守卫 =============

/**
 * 判断是否为单次任务
 */
export function isSingleTask(task: Task): boolean {
  return task.type === 'single'
}

/**
 * 判断是否为批量任务
 */
export function isBatchTask(task: Task): boolean {
  return task.type === 'batch'
}

/**
 * 判断 TaskItem 是否为 TTS Segment
 */
export function isTTSSegmentItem(item: TaskItem): item is TaskItem & { segment: TTSSegment } {
  return item.bizType === 'tts_segment' && !!item.segment
}

/**
 * 判断 TaskItem 是否为 Batch File
 */
export function isBatchFileItem(item: TaskItem): item is TaskItem & { file: BatchFile } {
  return item.bizType === 'batch_file' && !!item.file
}

// ============= 常量导出 =============

export const TASK_STATUS = {
  CREATED: 'created',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  POST_PROCESSING: 'post_processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PROCESSING_FAILED: 'processing_failed',
  POST_FAILED: 'post_failed',
  CANCELLED: 'cancelled'
} as const

export const TASK_TYPE = {
  SINGLE: 'single',
  BATCH: 'batch',
  DRAMA_RENDER: 'drama_render',
  DRAMA_EXPORT: 'drama_export'
} as const

export const BIZ_TYPE = {
  TTS_SEGMENT: 'tts_segment',
  BATCH_FILE: 'batch_file',
  DUBBING_LINE: 'dubbing_line'
} as const
